
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {ChartInfoUpdateResponse} from './ChartInfoUpdateResponse';

/**
 * ChartInfoUpdateRequest struct
 */
class ChartInfoUpdateRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!UUID=} dashboardId
   * @param {!UUID=} chartId
   * @param {!number=} version
   * @param {string=} newChartSettings
   * @constructor
   */
  constructor (parent = new Request(), dashboardId = new UUID(), chartId = new UUID(), version = new UInt64(0, 0), newChartSettings = null) {
    super()
    super.copy(parent)
    this.DashboardId = dashboardId
    this.ChartId = chartId
    this.Version = version
    this.NewChartSettings = newChartSettings
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ChartInfoUpdateRequest}
   * @param {!ChartInfoUpdateRequest} other Other struct
   * @returns {!ChartInfoUpdateRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.DashboardId != null) {
      this.DashboardId = new UUID(other.DashboardId)
    } else {
      this.DashboardId = null
    }
    if (other.ChartId != null) {
      this.ChartId = new UUID(other.ChartId)
    } else {
      this.ChartId = null
    }
    if (other.Version != null) {
      this.Version = UInt64.fromNumber(other.Version)
    } else {
      this.Version = null
    }
    if (other.NewChartSettings != null) {
      this.NewChartSettings = other.NewChartSettings
    } else {
      this.NewChartSettings = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ChartInfoUpdateRequest}
   * @returns {!ChartInfoUpdateRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ChartInfoUpdateRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ChartInfoUpdateRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ChartInfoUpdateRequest}
   * @param {!ChartInfoUpdateRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ChartInfoUpdateRequest)) {
      throw new TypeError('Instance of ChartInfoUpdateRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ChartInfoUpdateRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      DashboardId: ((this.DashboardId != null) ? this.DashboardId.toString() : null),
      ChartId: ((this.ChartId != null) ? this.ChartId.toString() : null),
      Version: ((this.Version != null) ? this.Version.toNumber() : null),
      NewChartSettings: ((this.NewChartSettings != null) ? this.NewChartSettings : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ChartInfoUpdateRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ChartInfoUpdateRequest} other Object value
   * @returns {!ChartInfoUpdateRequest} Created struct
   */
  static fromObject (other) {
    return new ChartInfoUpdateRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ChartInfoUpdateRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ChartInfoUpdateRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ChartInfoUpdateRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5193
  }
}

export { ChartInfoUpdateRequest };

/**
 * Fast Binary Encoding ChartInfoUpdateRequest field model
 */
class FieldModelChartInfoUpdateRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.DashboardId = new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.ChartId = new fbe.FieldModelUUID(buffer, this.DashboardId.fbeOffset + this.DashboardId.fbeSize)
    this.Version = new fbe.FieldModelUInt64(buffer, this.ChartId.fbeOffset + this.ChartId.fbeSize)
    this.NewChartSettings = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.Version.fbeOffset + this.Version.fbeSize), buffer, this.Version.fbeOffset + this.Version.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelChartInfoUpdateRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelChartInfoUpdateRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelChartInfoUpdateRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.DashboardId.fbeSize + this.ChartId.fbeSize + this.Version.fbeSize + this.NewChartSettings.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelChartInfoUpdateRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.DashboardId.fbeExtra + this.ChartId.fbeExtra + this.Version.fbeExtra + this.NewChartSettings.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelChartInfoUpdateRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelChartInfoUpdateRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelChartInfoUpdateRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5193
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelChartInfoUpdateRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelChartInfoUpdateRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.DashboardId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DashboardId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DashboardId.fbeSize

    if ((fbeCurrentSize + this.ChartId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ChartId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartId.fbeSize

    if ((fbeCurrentSize + this.Version.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Version.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Version.fbeSize

    if ((fbeCurrentSize + this.NewChartSettings.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewChartSettings.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewChartSettings.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelChartInfoUpdateRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelChartInfoUpdateRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelChartInfoUpdateRequest}
   * @param {!ChartInfoUpdateRequest} fbeValue Default value, defaults is new ChartInfoUpdateRequest()
   * @returns {!ChartInfoUpdateRequest} ChartInfoUpdateRequest value
   */
  get (fbeValue = new ChartInfoUpdateRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelChartInfoUpdateRequest}
   * @param {!ChartInfoUpdateRequest} fbeValue ChartInfoUpdateRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.DashboardId.fbeSize) <= fbeStructSize) {
      fbeValue.DashboardId = this.DashboardId.get()
    } else {
      fbeValue.DashboardId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DashboardId.fbeSize

    if ((fbeCurrentSize + this.ChartId.fbeSize) <= fbeStructSize) {
      fbeValue.ChartId = this.ChartId.get()
    } else {
      fbeValue.ChartId = new UUID()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChartId.fbeSize

    if ((fbeCurrentSize + this.Version.fbeSize) <= fbeStructSize) {
      fbeValue.Version = this.Version.get()
    } else {
      fbeValue.Version = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Version.fbeSize

    if ((fbeCurrentSize + this.NewChartSettings.fbeSize) <= fbeStructSize) {
      fbeValue.NewChartSettings = this.NewChartSettings.get()
    } else {
      fbeValue.NewChartSettings = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewChartSettings.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelChartInfoUpdateRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelChartInfoUpdateRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelChartInfoUpdateRequest}
   * @param {!ChartInfoUpdateRequest} fbeValue ChartInfoUpdateRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelChartInfoUpdateRequest}
   * @param {ChartInfoUpdateRequest} fbeValue ChartInfoUpdateRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.DashboardId.set(fbeValue.DashboardId)
    this.ChartId.set(fbeValue.ChartId)
    this.Version.set(fbeValue.Version)
    this.NewChartSettings.set(fbeValue.NewChartSettings)
  }
}

export { FieldModelChartInfoUpdateRequest };

/**
 * Fast Binary Encoding ChartInfoUpdateRequest model
 */
class ChartInfoUpdateRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelChartInfoUpdateRequest(this.buffer, 4)
  }

  /**
   * Get the ChartInfoUpdateRequest model
   * @this {!ChartInfoUpdateRequestModel}
   * @returns {!FieldModelChartInfoUpdateRequest} model ChartInfoUpdateRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ChartInfoUpdateRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ChartInfoUpdateRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ChartInfoUpdateRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ChartInfoUpdateRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelChartInfoUpdateRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ChartInfoUpdateRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ChartInfoUpdateRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ChartInfoUpdateRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ChartInfoUpdateRequestModel}
   * @param {!ChartInfoUpdateRequest} value ChartInfoUpdateRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ChartInfoUpdateRequestModel}
   * @param {!ChartInfoUpdateRequest} value ChartInfoUpdateRequest value, defaults is new ChartInfoUpdateRequest()
   * @return {!object} Deserialized ChartInfoUpdateRequest value and its size
   */
  deserialize (value = new ChartInfoUpdateRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ChartInfoUpdateRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ChartInfoUpdateRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ChartInfoUpdateRequestModel}
   * @param {!number} prev Previous ChartInfoUpdateRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ChartInfoUpdateRequestModel };
ChartInfoUpdateRequest.__has_response = true;
ChartInfoUpdateRequest.__response_class = ChartInfoUpdateResponse;
